<template>

  <v-row>
    <v-col cols="12">
      <v-table-toolbar>
        <v-spacer/>
        <v-btn-primary
          v-if="(store.hasAbility(Entity.Institution, Action.read) && store.hasAbility(Entity.Cohort, Action.create)) || isAdminOrRoot"
          prepend-icon="far fa-plus"
          @click="showCreateCohort = true"
          data-pw="createCohortButton"
        >
          {{ $t("buttons.create") }}
        </v-btn-primary>
      </v-table-toolbar>

    </v-col>
  </v-row>
  <v-row>
    <v-col md="3" lg="2">
      <table-filter-card @resetFilters="handleReset" :active-filter-count="activeFilterCount">
        <table-filter-card-item title="Search">
          <search-text-field
            :label="$t('common.search_title')"
            :disabled="store.networkBusy"
            :search-string="searchString"
            @update:text="text => {
              handleSearch(text)
            }"
          />
        </table-filter-card-item>
        <table-filter-card-item v-if="!hiddenFields.includes('tenant')" title="Tenant">
          <tenant-search-autocomplete
            v-model="selectedTenant"
            entity-type="tenant"
          />
        </table-filter-card-item>
        <table-filter-card-item v-if="!hiddenFields.includes('institution')" title="Institution">
          <tenant-search-autocomplete
            v-model="selectedInstitution"
            entity-type="institution"
            :tenant-ids="selectedTenant ? [selectedTenant] : []"
          />
        </table-filter-card-item>
      </table-filter-card>
    </v-col>
    <v-col md="9" lg="10">
      <v-data-table-server
        class="mb-6"
        density="compact"
        :loading="loadingData"
        :headers="headers"
        :items="cohorts"
        :items-per-page="perPage"
        :items-length="0"
        @click:row="onClickRow"
        @update:sort-by="onSort"
        :sort-by="sort"
        :hover="true"
        hide-default-footer
      >
        <template v-slot:no-data>
          <h5 class="pa-2 d-flex align-center justify-center align-items-center">
            {{ $t("common.no_data_message") }}
          </h5>
        </template>
        <template v-slot:loader>
          <v-sheet
            class="d-flex align-center justify-center"
            height="100%"
          >
            <v-progress-linear
              indeterminate
              color="primary"
            ></v-progress-linear>
          </v-sheet>
        </template>

        <template v-slot:item.yearGroup="{ item }">
          {{ item["exitYear"] ? getSchoolYearFromExitYear(item["exitYear"]) : 'Not Set' }}
        </template>

        <template v-slot:item.active="{ item }">
          <v-chip size="x-small" :color="item.active ? 'green' : 'red'" variant="outlined">
            {{ item.active ? 'Enabled' : 'Disabled' }}
          </v-chip>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ toLocaleDateTime(item["createdAt"]) }}
        </template>

        <template v-slot:bottom>
          <v-divider/>
          <pagination-controls
            v-if="paginatedData"
            class="text-center pt-2 v-table__pagination-controls"
            :pagination-footer-data="paginationFooterData"
            @update:per-page="onUpdatePerPage"
            @on-next="onNext"
            @on-prev="onPrev"
            @on-page-change="onPageChange"
            data-pw="institutionCohortsPaginationControls"
          />
        </template>
      </v-data-table-server>

    </v-col>
  </v-row>

  <v-dialog v-model="showCreateCohort" persistent max-width="500">
    <create-tenant-institution-cohort
      :entity-title="$t('components.CreateCards.CreateTenantInstitutionCohort.titleCreateCohort')"
      :entity-type="Entity.Cohort"
      :allowed="store.hasAbility(Entity.Cohort, Action.create) || isAdminOrRoot"
      :empty-cohort="true"
      data-pw="createCohortCard"
      @close="onCloseCreateDialog"
    />
  </v-dialog>
</template>

<script setup lang="ts">

import {onMounted, PropType, Ref, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {
  isYapiError,
  Paginated,
  Cohort,
  YapiError,
  CohortPaginationParams, Entity, Action, Role, Institution,
} from "@YenzaCT/sdk";

import yapi from "@/lib/yapi";
import {useGlobalStore} from "@/store";

import {ColumnsConfig, FooterData, footerDataFactory, usePagination} from "@/lib/pagination";
import PaginationControls from "@/components/PaginationControls.vue";
import SearchTextField from "@/components/SearchTextField.vue";
import {toLocaleDateTime} from "@/lib/time";
import CreateTenantInstitutionCohort from "@/components/CreateCards/CreateTenantInstitutionCohortCard.vue";
import {getSchoolYearFromExitYear} from "@/lib/util";
import TableFilterCardItem from "@/components/TableFilterCardItem.vue";
import TableFilterCard from "@/components/TableFilterCard.vue";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";

const router = useRouter();
const store = useGlobalStore();

const {t} = useI18n();
const route = useRoute();
const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);
const showCreateCohort = ref(false);
const loadingData = ref(false);
const cohorts: Ref<Cohort[]> = ref([]);
const paginatedData: Ref<Paginated<Cohort> | undefined> = ref();
let paginationFooterData: FooterData = footerDataFactory();
const searchString = ref(route.query.search as string);
const initialized = ref(false);

const props = defineProps({
  institution: {
    type: Object as PropType<Institution>,
    required: false
  },
  hiddenFields: {
    type: Array as PropType<string[]>,
    default: (): string[] => []
  },
  clickRoute: {
    type: String as PropType<string>,
    default: "cohortDetail"
  },
  routeParams: {
    type: Object as PropType<Record<string, unknown>>,
    default: () => ({})
  }
});

const cols: ColumnsConfig = {
  id: {
    title: t("components.CohortTablePage.table_headings._id"),
    modelKey: "_id",
    visible: false,
    filter: ""
  },
  title: {
    title: t("components.CohortTablePage.table_headings.title"),
    modelKey: "title",
    visible: true,
    filter: ""
  },
  slug: {
    title: t("components.CohortTablePage.table_headings.slug"),
    modelKey: "slug",
    visible: false,
    filter: ""
  },
  active: {
    title: "Active",
    modelKey: "active",
    visible: true,
    filter: ""
  },
  yearGroup: {
    title: "Year Group",
    modelKey: "yearGroup",
    visible: true,
    filter: ""
  },
  exitYear: {
    title: "Exit Year",
    modelKey: "exitYear",
    visible: true,
    filter: ""
  },
  tenant: {
    title: t("components.CohortTablePage.table_headings.tenant"),
    modelKey: "tenantMeta.title",
    visible: !props.hiddenFields.includes("tenant"),
    filter: ""
  },
  institution: {
    title: t("components.CohortTablePage.table_headings.institution"),
    modelKey: "institutionMeta.title",
    visible: !props.hiddenFields.includes("institution"),
    filter: ""
  },
  createdAt: {
    title: t("components.CohortTablePage.table_headings.created_at"),
    modelKey: "createdAt",
    visible: !props.hiddenFields.includes("cohort"),
    filter: ""
  }
};

const {
  columns,
  headers,
  page,
  perPage,
  sort,
  activeFilterCount,
  selectedTenant,
  selectedInstitution,
  createUrlFilterParameters,
  resetFilter,
  onUpdatePerPage,
  onSort,
  onNext,
  onPrev,
  onPageChange,
  setupFooterData,
  setupBasicParams
} = usePagination(fetchPage, {
  initialColumns: cols,
  initialSort: [{
    key: "createdAt",
    order: "asc"
  }]
});

async function fetchPage() {
  loadingData.value = true;

  const params: CohortPaginationParams = {
    page: page.value,
    size: perPage.value,
    sortBy: sort.value[0].key,
    sortOrder: sort.value[0].order,

    filterTenant: columns.tenant.filter as string || undefined,
    filterInstitution: columns.institution.filter as string || undefined,
  };
  params.title = columns.title.filter as string || undefined;

  try {
    store.networkBusy = true;
    paginatedData.value = (await yapi.admin.cohort.paginate(params)).data;
    await router.push({query: createUrlFilterParameters(params)});

    if (paginatedData.value) {
      cohorts.value = paginatedData.value.docs;
      paginationFooterData = setupFooterData(paginatedData.value);
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    loadingData.value = false;
    store.networkBusy = false;
  }
}

/**
 * Watch for changes to the search query param when using global search
 */
watch(() => route.query.search, async (newSearchValue) => {
  if (newSearchValue && newSearchValue !== searchString.value) {
    resetFilter(props.hiddenFields);
    handleSearch(newSearchValue as string);
  }
});

onMounted(async () => {
  loadingData.value = true;

  if (searchString.value) {
    handleSearch(searchString.value);
    return;
  }

  setupBasicParams(page, perPage, sort, route);
  columns.tenant.filter = route.query.filterTenant as string || columns.tenant.filter;
  selectedTenant.value = route.query.filterTenant as string || null;

  if (props.institution && props.institution._id) {
    columns.institution.filter = props.institution._id;
    selectedInstitution.value = props.institution._id;
  } else {
    columns.institution.filter = route.query.filterInstitution as string || columns.institution.filter;
    selectedInstitution.value = route.query.filterInstitution as string || null;
  }

  initialized.value = true;

  await fetchPage();

  loadingData.value = false;
});

const onClickRow = async (event: Event, row: { internalItem: { raw?: Cohort } }) => {
  if (row.internalItem && row.internalItem.raw) {
    const cohort = row.internalItem.raw;
    await navigateToCohort(cohort);
  }
};

const navigateToCohort = async (cohort: Cohort) => {
  const routeName = props.clickRoute || "cohortDetail";

  await router.push({
    name: routeName,
    params: {
      ...props.routeParams,
      cohortId: cohort._id
    }
  });
};

function handleSearch(searchText: string) {
  searchString.value = searchText;
  columns.title.filter = searchText;
}

function handleReset() {
  searchString.value = "";
  resetFilter(props.hiddenFields);

  if (props.institution && props.institution._id) {
    columns.institution.filter = props.institution._id;
  }
}

function onCloseCreateDialog() {
  showCreateCohort.value = false;
  fetchPage();
}
</script>

<style scoped>

</style>
